import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { CATEGORY_IMAGES } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
}

const Solutions = () => {
  const { data: categories, isLoading } = useQuery({
    queryKey: ["active-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name, slug, description, image_url")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as Category[];
    },
  });

  // Get category image - prioritize DB image_url, fallback to constants
  const getCategoryImage = (category: Category) => {
    if (category.image_url) return category.image_url;
    return CATEGORY_IMAGES[category.slug] || "/placeholder.svg";
  };

  return (
    <section id="soluciones" className="py-16 md:py-24 lg:py-32 section-alternate scroll-mt-24 relative">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 bg-nature-pattern opacity-40"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 md:mb-14 lg:mb-20">
          <div className="inline-flex items-center gap-2.5 bg-accent/80 border border-primary/15 text-primary rounded-full px-5 py-2.5 md:px-6 md:py-3 mb-6 md:mb-8 shadow-nature">
            <Leaf className="w-4 h-4 md:w-5 md:h-5" />
            <span className="text-xs md:text-sm font-semibold">Nuestras Soluciones</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-foreground mb-4 md:mb-6 lg:mb-8">
            Control Integral de Plagas
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
            Ofrecemos soluciones profesionales adaptadas a cada necesidad, con productos de la más alta calidad.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : categories && categories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
            {categories.map((category) => (
              <Card
                key={category.id}
                className="group overflow-hidden border border-border/50 shadow-nature hover:shadow-hover hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 rounded-2xl sm:rounded-3xl bg-card"
              >
                <div className="aspect-[16/10] overflow-hidden rounded-t-2xl sm:rounded-t-3xl bg-muted">
                  <img
                    src={getCategoryImage(category)}
                    alt={`${category.name} - Soluciones profesionales para control de plagas`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <CardContent className="p-4 sm:p-6 lg:p-7">
                  <h3 className="text-base sm:text-lg font-bold text-foreground mb-2 sm:mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {category.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6 line-clamp-2 leading-relaxed">
                    {category.description || "Soluciones profesionales de alta calidad."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full group/btn text-sm"
                    asChild
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <Link to={`/productos?category=${category.slug}`}>
                      Ver más
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            No hay categorías disponibles en este momento.
          </div>
        )}
      </div>
    </section>
  );
};

export default Solutions;
